import React, { createContext, useContext, useState } from "react";
import { LoanRepayment } from "../types/loanRepayment";
import {
    getLoanRepayments,
    createLoanRepayment,
    updateLoanRepayment,
    deleteLoanRepayment
} from "../api/loanRepaymentApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface LoanRepaymentContextProps {
    loanRepayments: LoanRepayment[];
    addLoanRepayment: (data: Omit<LoanRepayment, "id">) => Promise<LoanRepayment>;
    updateLoanRepayment: (data: LoanRepayment) => Promise<void>;
    deleteLoanRepayment: (id: string) => Promise<void>;
    fetchLoanRepayments: () => Promise<void>;
}

const LoanRepaymentContext = createContext<LoanRepaymentContextProps | undefined>(undefined);

export const LoanRepaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [loanRepayments, setLoanRepayments] = useState<LoanRepayment[]>([]);

    const fetchLoanRepayments = async () => {
        const list = await handleWithAlert(getLoanRepayments, { errorMessage: "Failed to fetch repayments" });
        if (list) setLoanRepayments(list.map((r: any) => ({ ...r, id: r._id || r.id })));
    };

    const addLoanRepayment = async (data: Omit<LoanRepayment, "id">) => {
        const created = await handleWithAlert(
            () => createLoanRepayment(data),
            { successMessage: "Repayment created!", errorMessage: "Failed to create", showSuccess: true }
        );
        if (created) fetchLoanRepayments();
        return created as LoanRepayment;
    };

    const updateLoanRepaymentFn = async (updated: LoanRepayment) => {
        const res = await handleWithAlert(
            () => updateLoanRepayment(updated.id!, updated),
            { successMessage: "Repayment updated!", errorMessage: "Failed to update", showSuccess: true }
        );
        if (res) fetchLoanRepayments();
    };

    const deleteLoanRepaymentFn = async (id: string) => {
        const res = await handleWithAlert(
            () => deleteLoanRepayment(id),
            { successMessage: "Repayment deleted!", errorMessage: "Failed to delete", showSuccess: true }
        );
        if (res !== null) setLoanRepayments(prev => prev.filter(r => r.id !== id));
    };

    return (
        <LoanRepaymentContext.Provider value={{
            loanRepayments,
            addLoanRepayment,
            updateLoanRepayment: updateLoanRepaymentFn,
            deleteLoanRepayment: deleteLoanRepaymentFn,
            fetchLoanRepayments
        }}>
            {children}
        </LoanRepaymentContext.Provider>
    );
};

export const useLoanRepayment = () => {
    const ctx = useContext(LoanRepaymentContext);
    if (!ctx) throw new Error("useLoanRepayment must be inside LoanRepaymentProvider");
    return ctx;
};
